import { FC, memo, useCallback, useState } from 'react';
import { Button, Flow, Radio, Message } from 'components';
import {
  ButtonStyle,
  ButtonType,
  FlowStrategy,
  InputType,
  MessageType,
  ServerMessageType,
} from 'enum/index';
import {
  LoginFlow,
  UiNode,
  UiNodeGroupEnum,
  UiNodeInputAttributes,
} from '@ory/client';
import { getErrors, onFilterFlowNodes } from 'helpers/index';
import { Error } from 'interfaces';

const AuthTypes = new Map([
  [UiNodeGroupEnum.LookupSecret, 'Backup recovery code'],
  [UiNodeGroupEnum.Totp, 'Authenticator application'],
  [UiNodeGroupEnum.Default, 'Default'],
  [UiNodeGroupEnum.Oidc, 'OpenID Connect'],
  [UiNodeGroupEnum.Webauthn, 'Security key'],
  [UiNodeGroupEnum.Password, 'Password'],
  [UiNodeGroupEnum.Code, 'Code'],
  [UiNodeGroupEnum.Link, 'Link'],
  [UiNodeGroupEnum.Profile, 'Profile'],
]);

interface SignInFormsProps {
  flow: LoginFlow;
}

export const SignInForms: FC<SignInFormsProps> = memo(
  ({ flow }: SignInFormsProps) => {
    const [aal2Flow, setAal2Flow] = useState<string>();
    const [aal2LoginStep, setAal2LoginStep] = useState<number>(0);
    const [mfaMethod, setMfaMethod] = useState<string>();

    const onPressNextButton = useCallback(() => {
      if (aal2Flow != undefined && aal2Flow !== FlowStrategy.Mfa) {
        setAal2LoginStep(aal2LoginStep + 1);
      }

      if (aal2Flow == FlowStrategy.Mfa) {
        const method = document.getElementsByName('method')[0];

        if (method) {
          const form = method.closest('form');

          const input = document.createElement('input');
          input.name = mfaMethod ?? 'email';
          input.hidden = true;
          input.value = '1';

          form && form.appendChild(input) && method.click();
        }
      }
    }, [aal2Flow, aal2LoginStep]);

    const filteredFlow = onFilterFlowNodes(
      flow!,
      FlowStrategy.SignIn,
    ) as LoginFlow;

    const errorMessage = getErrors(flow?.ui.messages || []);
    const error: Error = {
      text: undefined,
      type: ServerMessageType.Error,
    };

    if (errorMessage) {
      error.text = errorMessage;
    }

    const selectMethodMessage: Error = {
      text: 'Please select a method',
      type: ServerMessageType.Info,
    };

    const uniqueGroups: UiNodeGroupEnum[] = [];
    const uniqueNodes: UiNode[] = [];

    filteredFlow?.ui?.nodes.forEach((node) => {
      if (node.group !== UiNodeGroupEnum.Default) {
        if (
          (node.attributes as UiNodeInputAttributes)?.type ===
            InputType.SUBMIT &&
          (node.group as string) !== FlowStrategy.Mfa
        ) {
          uniqueNodes.push(node);
        } else if (
          ((node.attributes as UiNodeInputAttributes)?.type as string) ===
            InputType.RADIO &&
          (node.group as string) === FlowStrategy.Mfa
        ) {
          uniqueNodes.push(node);
        }

        if (uniqueGroups.indexOf(node.group) === -1) {
          uniqueGroups.push(node.group);
        }
      }
    });

    if (uniqueGroups.length === 1) {
      return (
        flow && (
          <Flow
            flowStrategy={FlowStrategy.SignIn}
            flow={onFilterFlowNodes(flow, uniqueGroups[0] as FlowStrategy)}
            activeStrategy={FlowStrategy.SignIn}
            loginChallenge={flow?.oauth2_login_request?.request_url}
          />
        )
      );
    } else if (aal2LoginStep == 0) {
      return (
        <>
          {<Message type={MessageType.Text} message={selectMethodMessage} />}
          {error.text && <Message type={MessageType.Text} message={error} />}

          <div className="py-3">
            {uniqueNodes.map((node, index) => (
              <Radio
                key={index}
                name="mfa_methods"
                id={`${node.group as string}_${
                  (node.attributes as UiNodeInputAttributes).name
                }`}
                value={(node.attributes as UiNodeInputAttributes).name}
                label={
                  ((node.attributes as UiNodeInputAttributes)
                    .type as string) === InputType.RADIO
                    ? `${node.meta.label?.text}`
                    : `${AuthTypes.get(node.group)}`
                }
                onChange={() => {
                  setAal2Flow(node.group as string);
                  setMfaMethod(
                    (node.attributes as UiNodeInputAttributes).name as string,
                  );
                }}
              />
            ))}
          </div>

          {flow && uniqueGroups.includes(FlowStrategy.Mfa as UiNodeGroupEnum) && (
            <Flow
              flowStrategy={FlowStrategy.SignIn}
              hideFlow={true}
              flow={onFilterFlowNodes(flow, FlowStrategy.Mfa)}
              activeStrategy={FlowStrategy.SignIn}
              loginChallenge={flow?.oauth2_login_request?.request_url}
            />
          )}

          <div className="pb-3">
            <Button
              type={ButtonType.BUTTON}
              onClick={onPressNextButton}
              name="Next"
              className={`${ButtonStyle.PRIMARY} w-100`}
            >
              Next
            </Button>
          </div>
        </>
      );
    } else if (flow && aal2Flow !== FlowStrategy.Mfa && aal2LoginStep == 1) {
      return (
        <Flow
          flowStrategy={FlowStrategy.SignIn}
          flow={onFilterFlowNodes(flow, aal2Flow as FlowStrategy)}
          showMessages={false}
          activeStrategy={FlowStrategy.SignIn}
          loginChallenge={flow?.oauth2_login_request?.request_url}
        />
      );
    }

    return <></>;
  },
);
